import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Chip,
  Divider,
  Autocomplete,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import type { ContractType, ContractClause, ContractTerm } from '../types';
import { dataService } from '../services/dataService';
import {
  contractService,
  STANDARD_CLAUSES,
  STANDARD_TERMS,
  contractTypeLabels,
} from '../services/contractService';
import { colors } from '../theme';

const ContractCreate: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState('');
  const [supplierName, setSupplierName] = useState<string | null>(null);
  const [type, setType] = useState<ContractType>('supply');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');

  // Clauses from template
  const [clauses, setClauses] = useState<(ContractClause & { selected: boolean })[]>(
    STANDARD_CLAUSES.map(c => ({ ...c, id: crypto.randomUUID(), selected: true }))
  );

  // Terms
  const [terms, setTerms] = useState<ContractTerm[]>(
    STANDARD_TERMS.map(t => ({ ...t, id: crypto.randomUUID() }))
  );

  // Available suppliers
  const [availableSuppliers, setAvailableSuppliers] = useState<string[]>([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Preview mode
  const [showPreview, setShowPreview] = useState(false);

  // =========================================================================
  // Load suppliers
  // =========================================================================

  useEffect(() => {
    const suppliers = dataService.getSuppliers();
    setAvailableSuppliers(suppliers);
  }, []);

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleSave = () => {
    // Validate
    if (!title.trim()) {
      setSnackbar({ open: true, message: 'Ingrese un título para el contrato', severity: 'error' });
      return;
    }
    if (!supplierName) {
      setSnackbar({ open: true, message: 'Seleccione un proveedor', severity: 'error' });
      return;
    }
    if (!startDate || !endDate) {
      setSnackbar({ open: true, message: 'Ingrese fechas de inicio y fin', severity: 'error' });
      return;
    }
    if (!value || parseFloat(value) <= 0) {
      setSnackbar({ open: true, message: 'Ingrese un valor válido para el contrato', severity: 'error' });
      return;
    }

    const selectedClauses = clauses
      .filter(c => c.selected)
      .map(({ selected: _, ...clause }) => clause);

    if (selectedClauses.length === 0) {
      setSnackbar({ open: true, message: 'Seleccione al menos una cláusula', severity: 'error' });
      return;
    }

    const contract = contractService.create({
      title: title.trim(),
      supplierId: `supplier-${supplierName.toLowerCase().replace(/\s+/g, '-')}`,
      supplierName,
      type,
      startDate,
      endDate,
      value: parseFloat(value),
      terms: terms.filter(t => t.value.trim() !== ''),
      clauses: selectedClauses,
      notes: notes.trim() || undefined,
    });

    setSnackbar({ open: true, message: `Contrato ${contract.contractNumber} creado exitosamente`, severity: 'success' });
    setTimeout(() => navigate(`/contracts/${contract.id}`), 1000);
  };

  const handleClauseToggle = (clauseId: string) => {
    setClauses(prev =>
      prev.map(c => {
        if (c.id === clauseId) {
          if (c.required) return c; // Can't deselect required clauses
          return { ...c, selected: !c.selected };
        }
        return c;
      })
    );
  };

  const handleClauseContentChange = (clauseId: string, content: string) => {
    setClauses(prev => prev.map(c => (c.id === clauseId ? { ...c, content } : c)));
  };

  const handleTermChange = (termId: string, field: 'description' | 'value', val: string) => {
    setTerms(prev => prev.map(t => (t.id === termId ? { ...t, [field]: val } : t)));
  };

  const handleAddTerm = () => {
    setTerms(prev => [...prev, { id: crypto.randomUUID(), description: '', value: '' }]);
  };

  const handleRemoveTerm = (termId: string) => {
    setTerms(prev => prev.filter(t => t.id !== termId));
  };

  const handleAddClause = () => {
    setClauses(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: 'Nueva Cláusula',
        content: '',
        required: false,
        selected: true,
      },
    ]);
  };

  // =========================================================================
  // Preview content
  // =========================================================================

  const renderPreview = () => {
    const selectedClauses = clauses.filter(c => c.selected);
    const validTerms = terms.filter(t => t.description.trim() && t.value.trim());

    return (
      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: colors.brand.blue, mb: 1 }}>
            INTERBARGE S.A.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title || 'Título del Contrato'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {contractTypeLabels[type]} — {supplierName || 'Proveedor'}
          </Typography>
          <Divider sx={{ mt: 2 }} />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Proveedor</Typography>
            <Typography variant="body1" fontWeight={600}>{supplierName || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Valor del Contrato</Typography>
            <Typography variant="body1" fontWeight={600}>
              {value ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value)) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Fecha de Inicio</Typography>
            <Typography variant="body1">{startDate || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Fecha de Fin</Typography>
            <Typography variant="body1">{endDate || '—'}</Typography>
          </Grid>
        </Grid>

        {validTerms.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Términos del Contrato
            </Typography>
            {validTerms.map((term) => (
              <Box key={term.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2">{term.description}</Typography>
                <Typography variant="body2" fontWeight={600}>{term.value}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
          Cláusulas ({selectedClauses.length})
        </Typography>
        {selectedClauses.map((clause, i) => (
          <Box key={clause.id} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {i + 1}. {clause.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {clause.content || '(Sin contenido)'}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Por INTERBARGE S.A.
            </Typography>
            <Box sx={{ borderTop: '1px solid #999', pt: 1 }}>
              <Typography variant="body2">Firma Autorizada</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Por {supplierName || 'EL PROVEEDOR'}
            </Typography>
            <Box sx={{ borderTop: '1px solid #999', pt: 1 }}>
              <Typography variant="body2">Firma Autorizada</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Breadcrumb + Header */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/contracts')}>
          Contratos LTP
        </Link>
        <Typography color="text.primary">Nuevo Contrato</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/contracts')}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Nuevo Contrato
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete los datos para crear un nuevo contrato con proveedor
            </Typography>
          </div>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Editar' : 'Vista Previa'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
          >
            Guardar Contrato
          </Button>
        </Box>
      </Box>

      {/* Preview Mode */}
      {showPreview && renderPreview()}

      {/* Edit Mode */}
      {!showPreview && (
        <>
          {/* Basic Data */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Datos Básicos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Título del Contrato"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Suministro Anual de Lubricantes Marinos"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Contrato"
                  value={type}
                  onChange={(e) => setType(e.target.value as ContractType)}
                >
                  <MenuItem value="service">Servicio</MenuItem>
                  <MenuItem value="supply">Suministro</MenuItem>
                  <MenuItem value="framework">Contrato Marco</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={availableSuppliers}
                  value={supplierName}
                  onChange={(_, newValue) => setSupplierName(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Proveedor" placeholder="Buscar proveedor..." required />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor del Contrato (USD)"
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Ej: 185000"
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography> }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Fecha Inicio"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Fecha Fin"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Notas"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Observaciones internas..."
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Terms */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Términos del Contrato
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddTerm}>
                Agregar Término
              </Button>
            </Box>
            {terms.map((term) => (
              <Box key={term.id} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Descripción"
                  value={term.description}
                  onChange={(e) => handleTermChange(term.id, 'description', e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Valor"
                  value={term.value}
                  onChange={(e) => handleTermChange(term.id, 'value', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Quitar término">
                  <IconButton size="small" color="error" onClick={() => handleRemoveTerm(term.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Paper>

          {/* Clauses */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <div>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Cláusulas del Contrato
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clauses.filter(c => c.selected).length} de {clauses.length} cláusulas seleccionadas — Las obligatorias no pueden desactivarse
                </Typography>
              </div>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddClause}>
                Agregar Cláusula
              </Button>
            </Box>

            {clauses.map((clause) => (
              <Accordion key={clause.id} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Checkbox
                      checked={clause.selected}
                      disabled={clause.required}
                      onChange={() => handleClauseToggle(clause.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: clause.selected ? 600 : 400, flex: 1 }}>
                      {clause.title}
                    </Typography>
                    {clause.required && (
                      <Chip label="Obligatoria" size="small" sx={{ fontSize: '0.7rem', bgcolor: colors.brand.blue, color: 'white' }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={clause.content}
                    onChange={(e) => handleClauseContentChange(clause.id, e.target.value)}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="text" onClick={() => navigate('/contracts')}>
              Cancelar
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(true)}
            >
              Vista Previa
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
            >
              Guardar Contrato
            </Button>
          </Box>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractCreate;
